import Icon from "./ui/Icon";
import { News } from "@/types";
import EmptyState from "./EmptyState";
import MediaViewer from "./MediaViewer";
import CategoryIcon from "./CatIconMapper";
import { useState, useEffect } from "react";
// import { Navigation } from 'swiper/modules'
import { Link, useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { toPersianDate } from "../utils/dateFormatter";
import { useNews } from "../features/news/hook/useNews";
import { isEmptyContent } from "../utils/isEmptyContent";
import { useSingleProject } from "../features/home/hooks/useSingleProject";
import { useToggleNewsFollow } from "../features/news/hook/useToggleNewsFollow";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

type NewsSectionProps = {
  form: "form1" | "form2";
  projectId?: number;
  excludeId?: number;
  showHeader?: boolean;
  showEmptyState?: boolean;
  limit?: number;
  layout?: "list" | "grid";
  noCardMargin?: boolean;
  newsData?: News[];
  showMedia?: boolean;
  isLoadingOverride?: boolean;
  categoryOverride?: any[];
};

export function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${breakpoint}px)`);

    const update = () => setIsMobile(media.matches);
    update();

    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [breakpoint]);

  return isMobile;
}

export default function NewsSection({
  form,
  projectId,
  excludeId,
  layout,
  limit,
  showHeader = true,
  showEmptyState = false,
  noCardMargin = false,
  newsData,
  showMedia = true,
  isLoadingOverride,
  categoryOverride,
}: NewsSectionProps) {
  const { id } = useParams();
  const effectiveProjectId = projectId ?? (id ? Number(id) : undefined);

  const { data: fallbackProject } = useSingleProject(
    categoryOverride ? undefined : effectiveProjectId,
  );
  const effectiveCategoryOverride =
    categoryOverride ?? fallbackProject?.categories;

  const finalExcludeId = excludeId;
  const { data: fetchedNews = [], isLoading: fetchedLoading } = useNews({
    projects: effectiveProjectId,
    ...(typeof finalExcludeId === "number" && !Number.isNaN(finalExcludeId)
      ? { exclude_id: finalExcludeId }
      : {}),
    enabled: !!effectiveProjectId && !newsData,
  });
  const isMobile = useIsMobile();
  const [activeSlides, setActiveSlides] = useState<Record<number, number>>({});
  const news = newsData ?? fetchedNews;
  const limitedNews = limit ? news.slice(0, limit) : news;
  const isLoading = isLoadingOverride ?? (newsData ? false : fetchedLoading);

  const NewsSkeleton = () => (
    <div className="flex flex-col h-[370px] bg-white/70 animate-pulse shadow-sm p-4 overflow-hidden rounded-xl xl:rounded-[20px]">
      <div className="flex w-full h-11 justify-between items-center shrink-0">
        <div className="flex items-start gap-x-1 xl:gap-x-2.5">
          <div className="size-5 xl:size-6 rounded bg-green-02 shrink-0" />
          <div className="space-y-1">
            <div className="w-16 h-2.5 bg-green-02 rounded" />
            <div className="w-12 h-2 bg-green-02/60 rounded" />
          </div>
        </div>
        <div className="w-8 h-4 bg-green-02 rounded shrink-0" />
      </div>
      <div className="w-full h-[184px] xl:h-48 bg-green-02/60 rounded-[20px] shadow-sm mt-2 shrink-0" />
      <div className="h-3 w-12 bg-green-02/60 rounded mt-3 shrink-0" />

      <div className="h-14 mt-1 flex flex-col justify-center gap-2 shrink-0">
        <div className="w-full h-3 bg-green-02 rounded" />
        <div className="w-4/5 h-3 bg-green-02/60 rounded" />
      </div>

      {/* Content - line-clamp-1 */}
      <div className="w-full h-3 bg-green-02/60 rounded mt-2 shrink-0" />
    </div>
  );

  const swiperBreakpoints = {
    0: { slidesPerView: 1, spaceBetween: 12 },
    640: { slidesPerView: 2, spaceBetween: 16, slidesPerGroup: 2 },
    768: { slidesPerView: 2, spaceBetween: 16, slidesPerGroup: 2 },
    1024: { slidesPerView: 3, spaceBetween: 20, slidesPerGroup: 3 },
    1280: { slidesPerView: 3.5, spaceBetween: 20, slidesPerGroup: 3 },
    1536: { slidesPerView: 4.5, spaceBetween: 20, slidesPerGroup: 4 },
    2560: { slidesPerView: 5.5, spaceBetween: 20, slidesPerGroup: 5 },
  };

  if (isLoading && form === "form2") {
    return (
      <Swiper
        pagination={{ clickable: true }}
        breakpoints={swiperBreakpoints}
        className="relative !px-5 xl:!ps-14 3xl:!px-5"
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <SwiperSlide key={i} className="w-full pb-1">
            <NewsSkeleton />
          </SwiperSlide>
        ))}
      </Swiper>
    );
  }

  if (isLoading && form === "form1") {
    return (
      <>
        {showHeader && (
          <h2 className="text-[16px] font-semibold text-accent-05 mb-3">
            تازه های اخبار این مجموعه
          </h2>
        )}
        <div className={`flex flex-col ${noCardMargin ? "gap-3" : ""}`}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={!noCardMargin ? "mt-3" : ""}>
              <NewsSkeleton />
            </div>
          ))}
        </div>
      </>
    );
  }

  if (!news.length) return showEmptyState ? <EmptyState /> : null;

  const Header =
    showHeader && form === "form1" ? (
      <h2 className="text-[16px] font-semibold text-accent-05">
        تازه های اخبار این مجموعه
      </h2>
    ) : null;

  return (
    <>
      {Header}

      {layout === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pb-0.5">
          {limitedNews.map((items) => {
            const activeIndex = activeSlides[items.id] ?? 0;

            return (
              <NewsCard
                key={items.id}
                items={items}
                form="form1"
                forceSlider={false}
                activeIndex={activeIndex}
                setActiveSlides={setActiveSlides}
                noCardMargin={noCardMargin}
                showMedia={showMedia}
                categoryOverride={effectiveCategoryOverride}
              />
            );
          })}
        </div>
      ) : form === "form1" || (form === "form2" && isMobile) ? (
        <div className={`flex flex-col ${noCardMargin ? "gap-3" : ""}`}>
          {limitedNews.slice(0, 3).map((items) => {
            const activeIndex = activeSlides[items.id] ?? 0;

            return (
              <div key={items.id} className={!noCardMargin ? "mt-3" : ""}>
                <NewsCard
                  key={items.id}
                  items={items}
                  form="form1"
                  forceSlider={form === "form1"}
                  activeIndex={activeIndex}
                  setActiveSlides={setActiveSlides}
                  noCardMargin={noCardMargin}
                  showMedia={showMedia}
                  categoryOverride={effectiveCategoryOverride}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <Swiper
          pagination={{ clickable: true }}
          noSwiping
          noSwipingClass="swiper-no-swiping"
          breakpoints={swiperBreakpoints}
          className="relative !px-5 xl:!ps-14 3xl:!px-5"
        >
          {limitedNews.map((items) => {
            const activeIndex = activeSlides[items.id] ?? 0;

            return (
              <SwiperSlide key={items.id} className="pb-1">
                <NewsCard
                  items={items}
                  form="form1"
                  forceSlider={false}
                  activeIndex={activeIndex}
                  setActiveSlides={setActiveSlides}
                  noCardMargin={noCardMargin}
                  showMedia={showMedia}
                  categoryOverride={effectiveCategoryOverride}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}
    </>
  );
}

type NewsCardProps = {
  items: any;
  showMedia?: boolean;
  form: "form1" | "form2";
  forceSlider?: boolean;
  activeIndex: number;
  setActiveSlides: React.Dispatch<React.SetStateAction<Record<number, number>>>;
  noCardMargin?: boolean;
  categoryOverride?: any[];
};

function NewsCard({
  items,
  showMedia = true,
  form,
  forceSlider = false,
  activeIndex,
  setActiveSlides,
  categoryOverride,
}: NewsCardProps) {
  const { mutate, isPending } = useToggleNewsFollow();
  const [isSaved, setIsSaved] = useState(items?.is_followed ?? false);

  const projects = Array.isArray(items.projects)
    ? items.projects
    : items.projects
      ? [items.projects]
      : [];

  const categories = Array.isArray(items.categories)
    ? items.categories
    : items.categories
      ? [items.categories]
      : [];

  const overrideCategories = Array.isArray(categoryOverride)
    ? categoryOverride
    : categoryOverride
      ? [categoryOverride]
      : [];

  const fallbackCategories =
    overrideCategories.length > 0 ? overrideCategories : categories;

  const normalizedProjects = projects.map((p: any) => ({
    ...p,
    categories: p.categories?.length
      ? Array.isArray(p.categories)
        ? p.categories
        : [p.categories]
      : fallbackCategories,
  }));

  const handleToggleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    mutate(items.id, {
      onSuccess: () => {
        setIsSaved((prev: any) => !prev);
      },
    });
  };

  if (form === "form2") {
    return (
      <div className="w-full">
        {showMedia && (
          <MediaViewer
            form={form}
            forceSlider={forceSlider}
            images={items.images}
            activeIndex={activeIndex}
            onChange={(index: number) =>
              setActiveSlides((prev) => ({ ...prev, [items.id]: index }))
            }
            enableMobileViewer
          />
        )}
      </div>
    );
  }
  const hasImages = Array.isArray(items.images) && items.images.length > 0;
  const shouldShowMedia = showMedia && hasImages;
  const secondaryText = [
    items.meta_text,
    items.subtitle,
    items.under_title,
  ].find(
    (value): value is string =>
      typeof value === "string" && !isEmptyContent(value),
  );
  const rawDate = items.date_time || items.release_date || items.date_release;
  const isIsoDate =
    rawDate instanceof Date ||
    (typeof rawDate === "string" &&
      /^\d{4}-\d{2}-\d{2}(?:[T\s]|$)/.test(rawDate.trim()));
  const displayDate = isIsoDate ? toPersianDate(rawDate) : rawDate || "";

  return (
    <Link
      to={`/news/${items.id}`}
      className={`flex flex-col ${
        showMedia ? "h-fit xl:h-[362px]" : "h-fit xl:h-[180px]"
      } bg-white hover:bg-white/70 transition shadow-sm h-fit p-[15px] xl:p-4 overflow-hidden rounded-[20px]`}
    >
      {/* Header */}
      <div className="flex w-full h-11 justify-between items-center gap-x-2.5">
        <div className="flex items-start gap-x-1 xl:gap-x-2.5">
          {normalizedProjects.map((p: any) => {
            return (
              <div
                key={`project-${p.id}`}
                className="flex gap-x-2.5 items-center"
              >
                {p.categories?.length > 0 ? (
                  p.categories.map((category: any) => (
                    <CategoryIcon
                      key={`project-${p.id}-icon-${category.id}`}
                      id={category.id}
                      className="size-5 xl:size-6 text-green-04"
                    />
                  ))
                ) : (
                  <CategoryIcon className="size-5 xl:size-6 text-green-05" />
                )}

                <div>
                  {p.categories?.map((cat: any) => (
                    <span
                      key={`project-${p.id}-cat-${cat.id}`}
                      className="font-estedad line-clamp-1 text-[11px] xl:text-xs font-semibold text-green-04 block"
                    >
                      {cat.name}
                    </span>
                  ))}

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="text-[11px] xl:text-xs font-normal font-estedad text-[#98B1AF] line-clamp-1">
                          {p.name}
                        </p>
                      </TooltipTrigger>

                      <TooltipContent className="font-estedad bg-green-02.5 text-green-05">
                        <p>{p.name}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            );
          })}
        </div>

        {/* Left actions */}
        <div className="flex items-center gap-x-3">
          {shouldShowMedia && (
            <div className="flex items-center border border-green-04 h-5 px-1 gap-x-1.5 rounded-md">
              <span className="flex flex-row-reverse items-center text-xs font-medium text-green-04 pt-1">
                <p className="font-bold">{activeIndex + 1}</p>
                <span className="font-light">{items.images.length}/</span>
              </span>
              <Icon icon="Camera" className="w-4 text-green-04" />
            </div>
          )}

          <button
            onClick={handleToggleSave}
            disabled={isPending}
            className="flex items-center justify-center"
          >
            <Icon
              icon={isSaved ? "Saved" : "Save"}
              className={`size-4 xl:size-[18px] transition ${
                isSaved ? "text-green-05" : "text-green-04"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Media */}
      {shouldShowMedia && (
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <MediaViewer
            form={form}
            forceSlider={forceSlider}
            images={items.images}
            activeIndex={activeIndex}
            onChange={(index) =>
              setActiveSlides((prev) => ({ ...prev, [items.id]: index }))
            }
            enableMobileViewer
          />
        </div>
      )}

      {/* Date */}
      <span className="h-3 text-xs font-normal text-accent-04/80 mt-3">
        {displayDate}
      </span>
      {!isEmptyContent(items.name) && (
        <div
          className={`estedad line-clamp-2 mt-1 ${
            form === "form1" ? "h-fit" : "h-fit"
          }`}
          dangerouslySetInnerHTML={{ __html: items.name || "" }}
        />
      )}
      {secondaryText && (
        <div
          className="estedad3 news-content line-clamp-1 pt-1 overflow-hidden break-words max-w-full"
          dangerouslySetInnerHTML={{ __html: secondaryText }}
        />
      )}
    </Link>
  );
}
